import { apiClient } from '@/shared/api/apiClient';
import { sendAnalyticsError } from '@/shared/utils/error';
import type { Chat } from 'quadratic-shared/typesAndSchemasAI';

/**
 * Backend-backed AI Analyst chat store. Chats are scoped to the file and shared
 * across all collaborators, so this is the source of truth for chat history
 * (the browser IndexedDB store is now only a best-effort local cache).
 *
 * Transcripts are persisted automatically by the AI chat endpoint on every
 * turn, so this module only needs to load history and propagate rename/delete.
 */
class AIAnalystServerChats {
  fileUuid?: string;

  private sendAnalyticsError = (from: string, error: Error | unknown) => {
    sendAnalyticsError('aiAnalystServerChats', from, error);
  };

  init = (fileUuid: string): void => {
    this.fileUuid = fileUuid;
  };

  loadChats = async (): Promise<Chat[]> => {
    if (!this.fileUuid) return [];
    try {
      const { chats } = await apiClient.ai.chats.list(this.fileUuid);
      return chats;
    } catch (error) {
      this.sendAnalyticsError('loadChats', error);
      throw error;
    }
  };

  renameChat = async (chatId: string, name: string): Promise<void> => {
    if (!this.fileUuid) return;
    try {
      await apiClient.ai.chats.rename(this.fileUuid, chatId, name);
    } catch (error) {
      this.sendAnalyticsError('renameChat', error);
    }
  };

  deleteChats = async (chatIds: string[]): Promise<void> => {
    if (!this.fileUuid || chatIds.length === 0) return;
    const fileUuid = this.fileUuid;
    try {
      await Promise.all(chatIds.map((chatId) => apiClient.ai.chats.delete(fileUuid, chatId)));
    } catch (error) {
      this.sendAnalyticsError('deleteChats', error);
    }
  };
}

export const aiAnalystServerChats = new AIAnalystServerChats();
